/**
 * The settings that change what the system DOES: how soon a client is chased, the wording they
 * receive, and the firm's documents.
 *
 * Its own component because the portal has TWO Settings screens — `realFirmSettings` for a signed-in
 * firm and `AdviserSettingsView` for the demo — and these belong in both. They were first written
 * into the demo one only, so a real firm could never reach them.
 *
 * Presentation follows the portal's existing template rows (coloured dot, name, "when this is sent")
 * rather than a stack of bare inputs: an adviser needs to know WHICH email they are editing and when
 * it goes out, and a wall of text boxes tells them neither.
 */
import { useState } from 'react';
import type { FirmSettings, MessageDefaults } from '../../services/tenantApi.ts';
import { TemplateAccordionRow } from './TemplateAccordionRow.tsx';

interface FirmWorkingPreferencesProps {
    settings?: FirmSettings;
    /** Our standard wording, from the server. SHOWN, not described: an adviser cannot judge whether
     *  to replace a message they have never read. */
    messageDefaults?: MessageDefaults | null;
    /** Absent in demo mode — the controls then render read-only rather than pretending to save. */
    onSave?: (patch: FirmSettings) => Promise<boolean>;
}

const FOLLOW_UP_CHOICES = [
    { value: '3', label: 'after 3 days' },
    { value: '5', label: 'after 5 days' },
    { value: '7', label: 'after 7 days' },
    { value: '14', label: 'after 14 days' },
    { value: 'manual', label: "never — I'll chase them myself" },
];

/** The placeholders a firm may use, shown as chips rather than buried in a sentence. */
const PLACEHOLDERS = ['{client_name}', '{adviser_name}', '{firm_name}', '{link}', '{expiry_date}'];

const cardStyle = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    marginTop: 14,
    overflow: 'hidden',
} as const;

const cardHeadStyle = { padding: '18px 20px 14px' } as const;
const titleStyle = { fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 } as const;
const subtitleStyle = { fontSize: 13, color: '#64748b', lineHeight: 1.55 } as const;

function PlaceholderChips() {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, marginBottom: 4 }}>
            {PLACEHOLDERS.map((p) => (
                <code
                    key={p}
                    style={{
                        fontSize: 11,
                        background: '#f1f5f9',
                        color: '#334155',
                        border: '1px solid #e2e8f0',
                        borderRadius: 5,
                        padding: '2px 6px',
                    }}
                >
                    {p}
                </code>
            ))}
        </div>
    );
}

export function FirmWorkingPreferences({ settings, messageDefaults, onSave }: FirmWorkingPreferencesProps) {
    const [followUp, setFollowUp] = useState(
        settings?.defaultFollowUpDays == null ? 'manual' : String(settings.defaultFollowUpDays),
    );
    const [templates, setTemplates] = useState({
        inviteSubject: settings?.templates?.invite?.subject ?? '',
        inviteBody: settings?.templates?.invite?.body ?? '',
        reminderSubject: settings?.templates?.reminder?.subject ?? '',
        reminderBody: settings?.templates?.reminder?.body ?? '',
    });
    const [documents, setDocuments] = useState({
        fsgUrl: settings?.fsgUrl ?? '',
        companyProfileUrl: settings?.companyProfileUrl ?? '',
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const touched = () => setSaved(false);
    const usingOwnWords = (subject: string, body: string) => Boolean(subject.trim() || body.trim());

    const save = async () => {
        if (!onSave) return;
        setSaving(true);
        setSaved(false);
        // Blank means "use our wording", not "send an empty email" — so only filled fields are sent.
        const pick = (subject: string, body: string) => {
            const t: { subject?: string; body?: string } = {};
            if (subject.trim()) t.subject = subject.trim();
            if (body.trim()) t.body = body.trim();
            return Object.keys(t).length ? t : undefined;
        };
        const invite = pick(templates.inviteSubject, templates.inviteBody);
        const reminder = pick(templates.reminderSubject, templates.reminderBody);
        const ok = await onSave({
            defaultFollowUpDays: followUp === 'manual' ? null : Number(followUp),
            templates: { ...(invite ? { invite } : {}), ...(reminder ? { reminder } : {}) },
            fsgUrl: documents.fsgUrl.trim(),
            companyProfileUrl: documents.companyProfileUrl.trim(),
        });
        setSaving(false);
        setSaved(ok);
    };

    const emailFields = (
        kind: 'invite' | 'reminder',
        subjectKey: 'inviteSubject' | 'reminderSubject',
        bodyKey: 'inviteBody' | 'reminderBody',
    ) => {
        const standard = messageDefaults?.[kind];
        const custom = usingOwnWords(templates[subjectKey], templates[bodyKey]);
        return (
            <>
                {/* What we actually send, in full. An adviser cannot judge whether to replace wording
                    they have never seen — and this is the same text the send paths fall back to, so it
                    is what their clients really receive rather than a description of it. */}
                {standard && !custom && (
                    <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>What we send now</div>
                            <button
                                type="button"
                                className="bsm bg"
                                style={{ fontSize: 11, padding: '4px 9px' }}
                                onClick={() => {
                                    // Copy it in, so they edit from our wording rather than a blank box.
                                    setTemplates((t) => ({ ...t, [subjectKey]: standard.subject, [bodyKey]: standard.body }));
                                    touched();
                                }}
                            >
                                Edit this wording
                            </button>
                        </div>
                        <div style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600, marginBottom: 6 }}>{standard.subject}</div>
                        <div style={{ fontSize: 12.5, color: '#475569', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{standard.body}</div>
                    </div>
                )}

                <div className="fg" style={{ marginTop: 14 }}>
                    <label className="fl">Subject</label>
                    <input
                        className="fi"
                        placeholder={standard ? standard.subject : 'Leave blank to use our standard subject'}
                        value={templates[subjectKey]}
                        onChange={(e) => {
                            setTemplates((t) => ({ ...t, [subjectKey]: e.target.value }));
                            touched();
                        }}
                    />
                </div>
                <div className="fg">
                    <label className="fl">Message</label>
                    <textarea
                        className="fi"
                        rows={7}
                        style={{ resize: 'vertical', lineHeight: 1.6 }}
                        placeholder={standard ? standard.body : 'Leave blank to use our standard message'}
                        value={templates[bodyKey]}
                        onChange={(e) => {
                            setTemplates((t) => ({ ...t, [bodyKey]: e.target.value }));
                            touched();
                        }}
                    />
                    <PlaceholderChips />
                    <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                        Your message must include <code>{'{link}'}</code>.{' '}
                        {custom
                            ? `Clear both fields to go back to our standard ${kind === 'invite' ? 'invitation' : 'reminder'}.`
                            : "Leave both blank and we'll send the wording shown above."}
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            <div style={cardStyle}>
                <div style={cardHeadStyle}>
                    <div style={titleStyle}>Chasing clients</div>
                    <div style={subtitleStyle}>
                        When a client hasn&rsquo;t opened their invite, we can send them a reminder for you.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                        <span style={{ fontSize: 13.5, color: '#0f172a' }}>Remind them</span>
                        <select
                            className="fi"
                            style={{ cursor: 'pointer', width: 'auto', minWidth: 220 }}
                            value={followUp}
                            onChange={(e) => {
                                setFollowUp(e.target.value);
                                touched();
                            }}
                            aria-label="How soon to chase a client who has not opened their invite"
                        >
                            {FOLLOW_UP_CHOICES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>
                        Applies to new invites; you can still change it for an individual client. We stop after three
                        reminders and leave the rest to you.
                    </div>
                </div>
            </div>

            <div style={cardStyle}>
                <div style={cardHeadStyle}>
                    <div style={titleStyle}>Client emails</div>
                    <div style={subtitleStyle}>
                        The two emails your clients receive. Leave them alone and we&rsquo;ll use our standard wording.
                    </div>
                </div>
                <div>
                    <TemplateAccordionRow
                        id={1}
                        dotColor="#3b82f6"
                        name={`Invitation${usingOwnWords(templates.inviteSubject, templates.inviteBody) ? ' — your wording' : ''}`}
                        meta="Sent when you invite a client to fill in their profile"
                    >
                        {emailFields('invite', 'inviteSubject', 'inviteBody')}
                    </TemplateAccordionRow>
                    <TemplateAccordionRow
                        id={2}
                        last
                        dotColor="#f59e0b"
                        name={`Reminder${usingOwnWords(templates.reminderSubject, templates.reminderBody) ? ' — your wording' : ''}`}
                        meta="Sent when you nudge a client, and by the automatic chase above"
                    >
                        {emailFields('reminder', 'reminderSubject', 'reminderBody')}
                    </TemplateAccordionRow>
                </div>
            </div>

            <div style={cardStyle}>
                <div style={cardHeadStyle}>
                    <div style={titleStyle}>Your documents</div>
                    <div style={subtitleStyle}>
                        Added to the bottom of every client email automatically, so changing the wording above can never
                        drop them. Links rather than uploads, so your client always gets the current version.
                    </div>
                    <div className="fg" style={{ marginTop: 14 }}>
                        <label className="fl">Financial Services Guide (FSG)</label>
                        <input
                            className="fi"
                            placeholder="https://yourfirm.com.au/fsg.pdf"
                            value={documents.fsgUrl}
                            onChange={(e) => {
                                setDocuments((d) => ({ ...d, fsgUrl: e.target.value }));
                                touched();
                            }}
                        />
                    </div>
                    <div className="fg">
                        <label className="fl">Company / practice profile</label>
                        <input
                            className="fi"
                            placeholder="https://yourfirm.com.au/about"
                            value={documents.companyProfileUrl}
                            onChange={(e) => {
                                setDocuments((d) => ({ ...d, companyProfileUrl: e.target.value }));
                                touched();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, marginBottom: 4 }}>
                <button type="button" className="bsm bp" disabled={!onSave || saving} onClick={save}>
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
                {saved && <span style={{ fontSize: 12.5, color: '#059669', fontWeight: 600 }}>Saved</span>}
                {!onSave && (
                    <span style={{ fontSize: 12.5, color: '#64748b' }}>Sign in to your firm to change these.</span>
                )}
            </div>
        </>
    );
}
